import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, Eye, Trash2 } from "lucide-react";
import PackAssignDropdown from "./PackAssignDropdown";
import CaptureIcon from "./IconComponent/CaptureIcon";
import MenuIconTooltip from "./MenuIconTooltip";
import { API_URL } from "../config/api";
import { MENU_LIBRARY_TOOLTIPS } from "../constants/menuTooltips";
import {
  deleteLibraryHighlight,
  fetchLibraryHighlightCounts,
  fetchLibraryHighlights,
} from "../utils/libraryApi";
import { excerptAroundQuote, isPartialWordQuote, locateQuoteStarts } from "../utils/libraryHighlightText";
import { chaptersToPlain } from "../utils/libraryPlainText";
import { sanitizeMotKey } from "../utils/wordSanitize";
import { openCaptureTab, showCaptureInTab } from "../utils/captureLink";
import { normalizeRegisterSearch } from "../utils/registerWordListDisplay";
import { readLibraryPlace, writeLibraryPlace } from "../utils/libraryReading";
import "./LibraryHighlightsView.css";

const highlightScrollMemory = new Map();

function highlightScrollKey(bookId, kind) {
  return `${bookId}:${kind}`;
}

function rememberHighlightScroll(key, scrollTop, allowZero = false) {
  const next = Math.max(0, Math.round(Number(scrollTop) || 0));
  const previous = highlightScrollMemory.get(key) || 0;
  if (!allowZero && next <= 0 && previous > 0) return previous;
  highlightScrollMemory.set(key, next);
  return next;
}

const LIST_TITLES = {
  passage: "Liste des passages importants",
  word: "Liste des mots à capturer",
  favorite: "Liste des passages favoris",
};

function highlightListCopy(kind) {
  if (kind === "word") {
    return {
      emptySearch: "Aucun mot ne correspond.",
      empty: "Aucun mot à capturer.",
      remove: MENU_LIBRARY_TOOLTIPS.removeWord,
      open: MENU_LIBRARY_TOOLTIPS.openWord,
    };
  }
  if (kind === "favorite") {
    return {
      emptySearch: "Aucun passage ne correspond.",
      empty: "Aucun passage favori.",
      remove: MENU_LIBRARY_TOOLTIPS.removeFavorite,
      open: MENU_LIBRARY_TOOLTIPS.openFavorite,
    };
  }
  return {
    emptySearch: "Aucun passage ne correspond.",
    empty: "Aucun passage important.",
    remove: MENU_LIBRARY_TOOLTIPS.removePassage,
    open: MENU_LIBRARY_TOOLTIPS.openPassage,
  };
}

const SORT_STORAGE_KEY = "vocabularium_library_highlight_sort";
const SORT_OPTIONS = [
  { value: "page", label: "chronologique: pages" },
  { value: "time", label: "chronologique: temps" },
];
const readingTextCache = new Map();
const quoteOrderCache = new Map();

function readSortMode() {
  try {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    if (saved === "time" || saved === "page") return saved;
  } catch {
    /* le tri par pages reste le choix de départ */
  }
  return "page";
}

function writeSortMode(mode) {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, mode);
  } catch {
    /* le tri reste valable pour cet affichage */
  }
}

function compareHighlightTime(a, b) {
  return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
}

function formatHighlightDate(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} à ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

async function loadReadingText(bookId) {
  if (readingTextCache.has(bookId)) return readingTextCache.get(bookId);
  const res = await fetch(`${API_URL}/library/books/${encodeURIComponent(bookId)}/reading`, {
    credentials: "include",
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "Impossible de lire le livre.");
  }
  let raw = await res.text();
  raw = raw.replace(/data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g, "");
  const json = JSON.parse(raw);
  const chapters = Array.isArray(json.chapters) ? json.chapters : [];
  const text = chaptersToPlain(chapters);
  readingTextCache.set(bookId, text);
  return text;
}

function orderForBook(bookId, text, items) {
  let known = quoteOrderCache.get(bookId);
  if (!known) {
    known = new Map();
    quoteOrderCache.set(bookId, known);
  }
  const missing = items.filter((item) => !known.has(item.id));
  if (missing.length) {
    const starts = locateQuoteStarts(text, missing);
    missing.forEach((item, index) => known.set(item.id, starts[index]));
  }
  return known;
}

function waitForPaint() {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

const PASSAGE_SKELETON = [
  { quote: "Gervaise se tournait les sangs", date: "27/09/2026 à 04:18", buttons: 2 },
  { quote: "les femmes étaient comme vous, on en épouserait dix", date: "27/09/2026 à 04:24", buttons: 2 },
  {
    quote:
      "c'était comme une lente conquête de la paresse, qui profitait de sa convalescence pour entrer dans sa peau et l'engourdir, en le chatouillant",
    date: "27/09/2026 à 04:26",
    buttons: 2,
  },
  { quote: "quitté la rue des Poissonniers", date: "27/09/2026 à 03:53", buttons: 2 },
  { quote: "loge de la grande maison, rue de la Goutte-d'Or", date: "27/09/2026 à 03:53", buttons: 2 },
  { quote: "rendez-vous avec le propriétaire", date: "27/09/2026 à 03:54", buttons: 2 },
];

const WORD_SKELETON = [
  { quote: "toquade", date: "27/09/2026 à 04:17", buttons: 2 },
  { quote: "roupie de sansonnet", date: "27/09/2026 à 04:18", buttons: 2 },
  { quote: "licheuse", date: "27/09/2026 à 04:18", buttons: 3 },
  { quote: "bourgerons", date: "27/09/2026 à 04:20", buttons: 3 },
  { quote: "palissandre", date: "27/09/2026 à 04:21", buttons: 3 },
  { quote: "sans épate", date: "27/09/2026 à 04:21", buttons: 3 },
];

function HighlightListSkeleton({ kind, fade, scrollerRef }) {
  const rows = kind === "word" ? WORD_SKELETON : PASSAGE_SKELETON;
  return (
    <div
      className={`library-highlights-scroll library-highlights-scroll--skel${
        fade.top ? " is-fade-top" : ""
      }${fade.bottom ? " is-fade-bottom" : ""}`}
      aria-busy="true"
    >
      <p className="library-highlights-skel__status">Chargement de la liste</p>
      <div className="library-highlights-scroll__body" ref={scrollerRef}>
        {rows.map((row) => (
          <div key={row.quote} className="library-highlights-list__item">
            <div className="library-highlights-list__text">
              <p className="library-highlights-list__quote">
                <span className="library-highlights-skel__ink">{row.quote}</span>
              </p>
              <p className="library-highlights-list__date">
                <span className="library-highlights-skel__ink">{row.date}</span>
              </p>
            </div>
            <span className="library-highlights-list__actions">
              {(row.buttons === 3 ? ["capture", "trash", "eye"] : ["trash", "eye"]).map((name) => (
                <span
                  key={name}
                  className="library-highlights-list__jump library-highlights-skel__icon"
                  aria-hidden="true"
                >
                  {name === "capture" ? (
                    <CaptureIcon size={16} color="currentColor" strokeWidth={1.75} />
                  ) : null}
                  {name === "trash" ? <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" /> : null}
                  {name === "eye" ? <Eye size={16} strokeWidth={1.75} aria-hidden="true" /> : null}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
      <span className="library-highlights-scroll__seal" aria-hidden="true" />
    </div>
  );
}

function bookLine(book) {
  const title = book?.title || "Sans titre";
  const author = String(book?.author || "").trim();
  return author ? `${title} (${author})` : title;
}

function highlightAnchor(item) {
  const quote = String(item?.quote || "").replace(/\s+/g, " ").trim();
  const before = String(item?.before || "").replace(/\s+/g, " ").trim();
  const after = String(item?.after || "").replace(/\s+/g, " ").trim();
  if (quote.length >= 24) return quote.slice(0, 120);
  return `${before.slice(-48)} ${quote} ${after.slice(0, 48)}`
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

function neighborhoodExcerpt(item) {
  return [item?.before, item?.quote, item?.after]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function HighlightList({
  book,
  kind,
  query = "",
  savedScroll = null,
  onBack,
  onOpenBook,
  onCaptureWord = null,
}) {
  const scrollerRef = useRef(null);
  const sortTouchRef = useRef(false);
  const [fade, setFade] = useState({ top: false, bottom: false });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortMode, setSortMode] = useState(readSortMode);
  const [pageOrder, setPageOrder] = useState(null);
  const [orderBookId, setOrderBookId] = useState("");
  const [pageOrderState, setPageOrderState] = useState("idle");
  const readyBookRef = useRef("");
  const [preparingId, setPreparingId] = useState("");
  const [capturedWords, setCapturedWords] = useState(null);
  const normalizedQuery = normalizeRegisterSearch(query);
  const activePageOrder =
    sortMode === "page" && pageOrderState === "ready" && orderBookId === book.id ? pageOrder : null;
  const visibleItems = useMemo(() => {
    const filtered = normalizedQuery
      ? items.filter((item) => normalizeRegisterSearch(item.quote).includes(normalizedQuery))
      : items;
    const list = [...filtered];
    if (activePageOrder) {
      list.sort((a, b) => {
        const left = activePageOrder.has(a.id) ? activePageOrder.get(a.id) : Number.POSITIVE_INFINITY;
        const right = activePageOrder.has(b.id) ? activePageOrder.get(b.id) : Number.POSITIVE_INFINITY;
        if (left !== right) return left - right;
        return compareHighlightTime(a, b);
      });
    } else {
      list.sort(compareHighlightTime);
    }
    return list;
  }, [activePageOrder, items, normalizedQuery]);
  const restoredScroll = useRef("");
  const capturePending = kind === "word" && capturedWords === null;
  const showSkeleton = !error && (loading || (capturePending && items.length > 0));

  useLayoutEffect(() => {
    const node = scrollerRef.current;
    if (!node || showSkeleton) return undefined;
    const key = highlightScrollKey(book.id, kind);
    const saved =
      typeof savedScroll === "number" && savedScroll > 0
        ? savedScroll
        : highlightScrollMemory.get(key);
    if (typeof saved !== "number" || saved <= 0) return undefined;
    rememberHighlightScroll(key, saved, true);
    let frames = 0;
    let raf = 0;
    const apply = () => {
      if (node.scrollHeight <= node.clientHeight) return;
      if (Math.abs(node.scrollTop - saved) > 1) node.scrollTop = saved;
      if (Math.abs(node.scrollTop - saved) <= 1) restoredScroll.current = key;
    };
    const tick = () => {
      apply();
      frames += 1;
      if (restoredScroll.current !== key && frames < 12) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [book.id, kind, showSkeleton, visibleItems.length, savedScroll]);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return undefined;
    const key = highlightScrollKey(book.id, kind);
    const updateFade = () => {
      const overflow = node.scrollHeight - node.clientHeight;
      setFade({
        top: node.scrollTop > 1,
        bottom: overflow - node.scrollTop > 1,
      });
    };
    if (showSkeleton) {
      updateFade();
      const observer = new ResizeObserver(updateFade);
      observer.observe(node);
      node.addEventListener("scroll", updateFade, { passive: true });
      return () => {
        node.removeEventListener("scroll", updateFade);
        observer.disconnect();
      };
    }
    const onScroll = () => {
      rememberHighlightScroll(key, node.scrollTop, true);
      restoredScroll.current = key;
      updateFade();
    };
    const onResize = () => {
      const saved = highlightScrollMemory.get(key);
      if (
        restoredScroll.current !== key &&
        typeof saved === "number" &&
        saved > 0 &&
        node.scrollHeight > node.clientHeight
      ) {
        node.scrollTop = saved;
        restoredScroll.current = key;
      } else if (node.scrollTop > 0) {
        rememberHighlightScroll(key, node.scrollTop);
      }
      updateFade();
    };
    onResize();
    node.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(onResize);
    observer.observe(node);
    return () => {
      rememberHighlightScroll(key, node.scrollTop);
      node.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [book.id, kind, normalizedQuery, visibleItems.length, showSkeleton, error]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchLibraryHighlights(book.id)
      .then((json) => {
        if (cancelled) return;
        const highlights = Array.isArray(json.highlights) ? json.highlights : [];
        const partials = highlights.filter(
          (item) => item.kind === "word" && isPartialWordQuote(item.after)
        );
        partials.forEach((item) => {
          deleteLibraryHighlight(book.id, item.id).catch(() => {});
        });
        const matching = highlights.filter(
          (item) => item.kind === kind && item.quote && !partials.includes(item)
        );
        setItems(matching);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Impossible de lire les surlignages.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [book.id, kind]);

  useEffect(() => {
    if (kind !== "word") return undefined;
    let cancelled = false;
    fetch(`${API_URL}/mots-liste`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const known = new Set(
          (Array.isArray(list) ? list : [])
            .map((entry) => sanitizeMotKey(entry))
            .filter(Boolean)
        );
        setCapturedWords(known);
      })
      .catch(() => {
        if (!cancelled) setCapturedWords(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  useEffect(() => {
    if (sortMode !== "page" || !items.length) return undefined;
    let cancelled = false;
    const snapshot = items;
    const requestedBookId = book.id;
    if (readyBookRef.current !== requestedBookId) setPageOrderState("loading");
    loadReadingText(requestedBookId)
      .then(async (text) => {
        if (cancelled) return;
        await waitForPaint();
        if (cancelled) return;
        const known = orderForBook(requestedBookId, text, snapshot);
        const order = new Map();
        snapshot.forEach((item) => {
          const start = known.get(item.id);
          if (start == null) return;
          order.set(item.id, start);
        });
        readyBookRef.current = requestedBookId;
        setPageOrder(order);
        setOrderBookId(requestedBookId);
        setPageOrderState("ready");
      })
      .catch(() => {
        if (cancelled || readyBookRef.current === requestedBookId) return;
        setPageOrderState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [book.id, items, sortMode]);

  const prepareCapture = async (item) => {
    if (!onCaptureWord || preparingId) return;
    const tab = openCaptureTab();
    setPreparingId(item.id);
    let extrait = "";
    try {
      const text = await loadReadingText(book.id);
      extrait = excerptAroundQuote(text, item);
    } catch {
      extrait = "";
    }
    if (!extrait) extrait = neighborhoodExcerpt(item);
    const draft = {
      mot: item.quote,
      livre: book?.title || "",
      auteur: book?.author || "",
      extrait,
    };
    setPreparingId("");
    if (!showCaptureInTab(tab, draft)) onCaptureWord(draft);
  };

  const chooseSort = (mode) => {
    if (mode !== "page" && mode !== "time") return;
    if (mode === sortMode) return;
    sortTouchRef.current = true;
    writeSortMode(mode);
    setSortMode(mode);
  };

  useLayoutEffect(() => {
    if (!sortTouchRef.current) return;
    sortTouchRef.current = false;
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollTop = 0;
    rememberHighlightScroll(highlightScrollKey(book.id, kind), 0, true);
  }, [book.id, kind, sortMode]);

  const listCopy = highlightListCopy(kind);

  const removeHighlight = (item) => {
    const previous = items;
    setItems((current) => current.filter((entry) => entry.id !== item.id));
    setError("");
    deleteLibraryHighlight(book.id, item.id).catch((err) => {
      setItems(previous);
      setError(err.message || "Impossible de retirer le surlignage.");
    });
  };

  return (
    <div className="library-highlights-list">
      <button type="button" className="library-highlights-list__back" onClick={onBack}>
        <ChevronLeft size={18} strokeWidth={1.75} aria-hidden="true" />
        Retour
      </button>
      <h1 className="libraryView__title">{LIST_TITLES[kind]}</h1>
      <p className="library-highlights-list__book">{bookLine(book)}</p>
      <div className="library-highlights-list__sort">
        <PackAssignDropdown
          options={SORT_OPTIONS}
          value={sortMode}
          onChange={chooseSort}
          ariaLabel="Trier les surlignages"
          matchTriggerWidth
          openBelow
          menuMaxHeight={120}
        />
      </div>
      {!showSkeleton && sortMode === "page" && pageOrderState === "loading" ? (
        <p className="library-highlights-list__sort-note">Classement selon les pages…</p>
      ) : null}
      {!showSkeleton && sortMode === "page" && pageOrderState === "error" ? (
        <p className="library-highlights-list__sort-note">
          L'ordre des pages est indisponible. La liste reste dans l'ordre du temps.
        </p>
      ) : null}
      {error ? <p className="libraryView__empty">{error}</p> : null}
      {showSkeleton ? (
        <HighlightListSkeleton kind={kind} fade={fade} scrollerRef={scrollerRef} />
      ) : null}
      {!showSkeleton && !loading && !error && !visibleItems.length ? (
        <p className="libraryView__empty">
          {normalizedQuery ? listCopy.emptySearch : listCopy.empty}
        </p>
      ) : null}
      {!showSkeleton && !loading && !error && visibleItems.length ? (
        <div
          className={`library-highlights-scroll${fade.top ? " is-fade-top" : ""}${
            fade.bottom ? " is-fade-bottom" : ""
          }`}
        >
          <div className="library-highlights-scroll__body" ref={scrollerRef}>
            {visibleItems.map((item) => {
              const dateLabel = formatHighlightDate(item.createdAt);
              return (
              <div key={item.id} className="library-highlights-list__item">
                <div className="library-highlights-list__text">
                  <p className="library-highlights-list__quote">{item.quote}</p>
                  {dateLabel ? <p className="library-highlights-list__date">{dateLabel}</p> : null}
                </div>
                <span className="library-highlights-list__actions">
                  {kind === "word" &&
                  capturedWords &&
                  !capturedWords.has(sanitizeMotKey(item.quote)) ? (
                    <MenuIconTooltip
                      tooltip={MENU_LIBRARY_TOOLTIPS.captureWord}
                      placement="above"
                      portal
                    >
                      <button
                        type="button"
                        className="library-highlights-list__jump"
                        aria-label={MENU_LIBRARY_TOOLTIPS.captureWord}
                        disabled={preparingId === item.id}
                        onClick={() => prepareCapture(item)}
                      >
                        <CaptureIcon
                          size={16}
                          color="currentColor"
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                      </button>
                    </MenuIconTooltip>
                  ) : null}
                  <MenuIconTooltip tooltip={listCopy.remove} placement="above" portal>
                    <button
                      type="button"
                      className="library-highlights-list__jump library-highlights-list__delete"
                      aria-label={listCopy.remove}
                      onClick={() => removeHighlight(item)}
                    >
                      <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </MenuIconTooltip>
                  <MenuIconTooltip tooltip={listCopy.open} placement="above" portal>
                  <button
                    type="button"
                    className="library-highlights-list__jump"
                    aria-label={listCopy.open}
                    onClick={() => {
                      const node = scrollerRef.current;
                      const scrollTop = node
                        ? rememberHighlightScroll(highlightScrollKey(book.id, kind), node.scrollTop, true)
                        : 0;
                      const place = readLibraryPlace(book.id);
                      writeLibraryPlace(book.id, {
                        anchor: highlightAnchor(item),
                        fontSize: place?.fontSize,
                        percent: place?.percent,
                      });
                      onOpenBook(book.id, { scrollTop });
                    }}
                  >
                    <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
                  </button>
                  </MenuIconTooltip>
                </span>
              </div>
              );
            })}
          </div>
          <span className="library-highlights-scroll__seal" aria-hidden="true" />
        </div>
      ) : null}
    </div>
  );
}

function CountCell({ count, label, onOpen }) {
  return (
    <td className="library-highlights__count">
      <span className="library-highlights__pair">
        <span>{count}</span>
        {count > 0 ? (
          <button
            type="button"
            className="library-highlights__eye"
            aria-label={label}
            onClick={onOpen}
          >
            <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : (
          <span className="library-highlights__eye-slot" aria-hidden="true" />
        )}
      </span>
    </td>
  );
}

function resumeDetail(books, resume) {
  if (!resume?.bookId || !resume.kind) return null;
  const book = books.find((item) => item.id === resume.bookId);
  if (!book) return null;
  const scrollTop = Math.max(0, Math.round(Number(resume.scrollTop) || 0));
  return { book, kind: resume.kind, scrollTop };
}

export default function LibraryHighlightsView({
  books = [],
  loadingBooks = false,
  query = "",
  resume = null,
  onResumeApplied = () => {},
  onClose = () => {},
  onOpenBook = () => {},
  onCaptureWord = null,
  onKindChange = () => {},
}) {
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(() => resumeDetail(books, resume));
  const [countsToken, setCountsToken] = useState(0);

  useLayoutEffect(() => {
    if (!resume?.bookId || !resume.kind) return;
    const next = resumeDetail(books, resume);
    if (!next) return;
    setDetail((current) =>
      current?.book?.id === next.book.id && current.kind === next.kind ? current : next
    );
    onResumeApplied();
  }, [books, resume, onResumeApplied]);

  useEffect(() => {
    let cancelled = false;
    if (countsToken === 0) setLoading(true);
    setError("");
    fetchLibraryHighlightCounts()
      .then((json) => {
        if (!cancelled) setCounts(Array.isArray(json.counts) ? json.counts : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Impossible de lire les surlignages.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [countsToken]);

  useEffect(() => {
    onKindChange(detail?.kind || null);
  }, [detail, onKindChange]);

  useEffect(() => () => onKindChange(null), [onKindChange]);

  const byBook = new Map(counts.map((item) => [item.bookId, item]));
  const rows = books
    .map((book) => ({ book, count: byBook.get(book.id) }))
    .filter(
      (row) =>
        row.count && (row.count.passages > 0 || row.count.words > 0 || (row.count.favorites || 0) > 0)
    )
    .sort((a, b) =>
      String(a.book.title || "").localeCompare(String(b.book.title || ""), "fr")
    );

  if (detail) {
    return (
      <HighlightList
        book={detail.book}
        kind={detail.kind}
        savedScroll={detail.scrollTop}
        onBack={() => {
          setDetail(null);
          setCountsToken((value) => value + 1);
        }}
        query={query}
        onOpenBook={onOpenBook}
        onCaptureWord={onCaptureWord}
      />
    );
  }

  return (
    <>
      <header className="libraryView__head libraryView__head--highlights">
        <button type="button" className="libraryView__library" onClick={onClose}>
          <ChevronLeft size={16} strokeWidth={1.75} aria-hidden="true" />
          Bibliothèque
        </button>
        <h1 className="libraryView__title">Surlignements dans vos livres</h1>
      </header>
      {loading || loadingBooks ? (
        <p className="libraryView__empty">Chargement des surlignages…</p>
      ) : null}
      {error ? <p className="libraryView__empty">{error}</p> : null}
      {!loading && !loadingBooks && !error ? (
        <table className="library-highlights">
          <thead>
            <tr>
              <th>Titre de l'ouvrage</th>
              <th>Passages importants</th>
              <th>Mots à capturer</th>
              <th>Passages favoris</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map(({ book, count }) => (
                <tr key={book.id}>
                  <td>{book.title}</td>
                  <CountCell
                    count={count.passages}
                    label={`Voir les passages importants de ${book.title}`}
                    onOpen={() => setDetail({ book, kind: "passage" })}
                  />
                  <CountCell
                    count={count.words}
                    label={`Voir les mots à capturer de ${book.title}`}
                    onOpen={() => setDetail({ book, kind: "word" })}
                  />
                  <CountCell
                    count={count.favorites || 0}
                    label={`Voir les passages favoris de ${book.title}`}
                    onOpen={() => setDetail({ book, kind: "favorite" })}
                  />
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="library-highlights__empty">
                  Aucun surlignage.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      ) : null}
    </>
  );
}
